import { isEmpty } from "lodash";
import {
  Badge,
  Divider,
  Flex,
  H4,
  StyledLink,
  Icon,
  Popover,
} from "orcs-design-system";
import PropTypes from "prop-types";
import { useHistory } from "react-router";
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import pluralize from "pluralize";
import { teamDetailsPath } from "src/util/routing";
import { TEAM_TAB } from "src/consts/urlPaths";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import TeamAvatar from "src/components/TeamAvatar/TeamAvatar";
import TeamDetailsActionMenu from "src/components/TeamDetailPage/ActionsMenu";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import TagDisplay from "src/components/TagDisplay";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import useRestricted from "src/app/Restricted/useRestricted";
import { ACTION_TYPES } from "src/components/OrgChart/context/actionTypes";
import NodeDetailsIcon from "src/components/NodeVisualizer/components/NodeDetailsIcon";
import { isReadOnly } from "src/components/OrgChart/context/utils/getCalculatedState";
import { EventBus } from "src/services/eventEmitter";
import {
  PANEL_ID,
  ORG_CHART_PANEL_EVENTS,
} from "src/components/OrgChart/components/OrgChartPanel/const";
import GroupTypeBadge from "../../../Badge/GroupTypeBadge";
import {
  EDGE_COLOR,
  SourceHandle,
  StyledCard,
  TargetHandle,
  DIFF_STATE_THEMES,
  DEFAULT_CARD_THEME,
  changes,
  StyledSimpleTotals,
} from "../node.styled";
import { RoundIconButton } from "../RoundIconButton";
import { useNodeProperties } from "../../hooks/useNodeProperties";
import { applySelectedTheme } from "../utils/applySelectedTheme";
import { useNodeSelection } from "../utils/useNodeSelection";
import GroupNodeSkeleton from "./GroupNodeSkeleton";

const StyledButton = styled(RoundIconButton)`
  position: absolute;
  left: calc(50% - 10px);
  bottom: -10px;
`;

const ExpandUpwardsButton = styled(RoundIconButton)`
  position: absolute;
  left: calc(50% - 10px);
  top: -40px;
`;

const DottedLine = styled.div`
  position: absolute;
  top: -30px;
  left: calc(50% - 1.5px);

  border-left: 2px solid ${EDGE_COLOR};
  height: 30px;
  width: 1px;
`;

const THEMES = {
  default: DEFAULT_CARD_THEME,
  diffStates: DIFF_STATE_THEMES,
};

const getTheme = (diffState, isSelected = false) => {
  const baseTheme = diffState ? THEMES.diffStates[diffState] : THEMES.default;

  return applySelectedTheme(baseTheme, isSelected);
};

const GroupNode = ({ data, ...props }) => {
  const [isHovered, setIsHovered] = useState(false);

  const workspace = useWorkspace();
  const tagsToBeDisplayed = useNodeProperties({
    tags: data?.tags,
    entityType: ENTITY_TYPES.GROUP,
  });

  const [isDirectoryUser] = useRestricted();

  const history = useHistory();

  const {
    loadTeamDataById,
    state,
    toggleExpandedNode,
    dispatch: orgChartDispatch,
  } = useOrgChartContext();
  const viewOptions = state?.viewOptions;

  const isCurrentTeam = useMemo(
    () => state?.currentViewTeam?.id === data?.id,
    [state?.currentViewTeam?.id, data?.id]
  );

  const isExpanded = useMemo(
    () => state?.expandedNodes?.includes(data?.id),
    [state?.expandedNodes, data?.id]
  );

  const isSelected = useNodeSelection(state, data.id);

  const handleIconClick = useCallback(() => {
    if (isCurrentTeam) {
      return;
    }

    if (state?.expandedNodes?.includes(data?.id)) {
      toggleExpandedNode(data?.id);
    } else {
      loadTeamDataById(data.id);
    }
  }, [
    data,
    loadTeamDataById,
    isCurrentTeam,
    state?.expandedNodes,
    toggleExpandedNode,
  ]);

  const onExpandUpwardsClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!data?.directParentId) {
        return;
      }
      const teamPath = teamDetailsPath(data.directParentId, TEAM_TAB.ORG_CHART);
      if (teamPath) {
        history.push(teamPath);
      }
    },
    [data?.directParentId, history]
  );

  const handleGroupClick = useCallback(() => {
    // Orcs FloatingPanels listens to this event
    EventBus.emit(ORG_CHART_PANEL_EVENTS.SET_SELECTED_PANEL, PANEL_ID.details);

    const isEntitySelected =
      state?.expandedPanel?.data?.selectedEntity === data.id;
    // not selected?
    if (orgChartDispatch && !isEntitySelected) {
      // select
      orgChartDispatch({
        type: ACTION_TYPES.UPDATE_EXPANDED_PANEL,
        payload: {
          data: {
            selectedEntity: data.id,
          },
        },
      });
    }
  }, [data, orgChartDispatch, state?.expandedPanel?.data?.selectedEntity]);

  const readOnly = useMemo(() => isReadOnly(data), [data]);

  const [membersCount, membersCountText, teamsCount, teamsCountText] =
    useMemo(() => {
      const indirectMemberCount = data?.memberCount ?? 0;
      const indirectChildTeamCount = data?.childTeamCount ?? 0;

      return [
        indirectMemberCount,
        pluralize("member", indirectMemberCount),
        indirectChildTeamCount,
        pluralize("team", indirectChildTeamCount),
      ];
    }, [data?.memberCount, data?.childTeamCount]);

  const totalCountTooltipText = useMemo(() => {
    const memberNum = membersCount.toLocaleString();
    const teamNum = teamsCount.toLocaleString();

    return `${memberNum} ${membersCountText} | ${teamNum} ${teamsCountText}`;
  }, [membersCount, membersCountText, teamsCount, teamsCountText]);

  if (data?.isLoading || isEmpty(data)) {
    return <GroupNodeSkeleton />;
  }

  return (
    <StyledCard
      onClick={handleGroupClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
      width="400px"
      $hidden={data?.hidden}
      cursor="grab"
      $theme={getTheme(data?.diffType, isSelected)}
      className={`${data?.state?.toLowerCase()} ${props.className || ""}`}
      key={`${data?.directParentId}-${data?.id}`}
      active={isSelected}
    >
      <TargetHandle $hidden type="target" position="top" />
      {isCurrentTeam && data?.directParentId && (
        <>
          <DottedLine />
          <ExpandUpwardsButton
            className="tree-navigator light"
            icon={["fas", "chevron-up"]}
            onClick={onExpandUpwardsClick}
            ariaLabel="Navigate up to parent team"
          />
        </>
      )}
      <Flex alignItems="flex-start" justifyContent="space-between">
        <Flex alignItems="center">
          <TeamAvatar group={data} sizing="large" mr="r" />
          <Flex alignItems="flex-start" flexDirection="column" gap="4px">
            <StyledLink
              to={teamDetailsPath(data?.id, TEAM_TAB.ORG_CHART)}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <H4 breakWord>{data?.name}</H4>
            </StyledLink>
            <GroupTypeBadge
              group={data}
              groupTypes={workspace?.config?.groupTypes}
            />
          </Flex>
        </Flex>
        {!isDirectoryUser && !readOnly && (
          <TeamDetailsActionMenu
            team={data}
            showInsightIcon={false}
            direction="right-start"
            runGroupCacheUpdateOnAddition={false}
            showChangesEnabled={viewOptions?.showChanges}
            onChangeDetailsClick={() => {
              handleGroupClick();
              // wait for the panel to open
              setTimeout(() => {
                EventBus.emit(ORG_CHART_PANEL_EVENTS.EDIT_TEAM_DETAILS);
              }, 100);
            }}
          />
        )}
      </Flex>
      <Flex mt="s" justifyContent="space-between" alignItems="center">
        <Popover
          text={totalCountTooltipText}
          direction="top"
          width="fit-content"
        >
          <StyledSimpleTotals>
            {membersCount.toLocaleString()}
            <Icon icon="user" size="sm" color="greyDarker" />
            {teamsCount.toLocaleString()}
            <Icon icon="users" size="sm" color="greyDarker" />
          </StyledSimpleTotals>
        </Popover>
        <NodeDetailsIcon
          isSelected={isSelected}
          isHovered={isHovered}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleGroupClick();
          }}
        />
      </Flex>

      {data?.diffType && (
        <>
          <Divider my="s" />
          <Badge variant={DIFF_STATE_THEMES[data?.diffType]?.color} altStyle>
            {changes[data?.diffType]}
          </Badge>
        </>
      )}

      {!isEmpty(tagsToBeDisplayed) && (
        <>
          <Divider my="s" />
          <TagDisplay tags={tagsToBeDisplayed} />
        </>
      )}
      <SourceHandle $hidden type="source" position="bottom" />
      {!isCurrentTeam && data?.isExpandable && (
        <StyledButton
          className="tree-navigator"
          icon={["fas", isExpanded ? "chevron-up" : "chevron-down"]}
          ariaLabel={`Click to ${isExpanded ? "collapse" : "expand"} this team`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleIconClick();
          }}
        />
      )}
    </StyledCard>
  );
};

GroupNode.propTypes = {
  data: PropTypes.object,
  className: PropTypes.string,
};

export default React.memo(GroupNode);
